// src/utils/imageProxy.js
import { isMobileDevice, shouldUseDirectImageUrls, isWeChatBrowser } from './deviceDetection';

/**
 * Adds a CORS proxy prefix to a URL for development environment
 * and adds cache busting for Firebase Storage URLs
 * @param {string} url - The original URL
 * @returns {string} - URL with CORS proxy prefix if in development and cache busting
 */
export const getCorsProxyUrl = (url) => {
  // Skip processing for blob URLs - they're already same-origin and don't need proxying
  if (url && url.startsWith('blob:')) {
    return url;
  }
  
  // Add cache busting for Firebase Storage URLs
  let processedUrl = url;
  if (url && (url.includes('firebasestorage.googleapis.com') || url.includes('fotocarnetmadrid.firebasestorage.app'))) {
    // Add a timestamp parameter to prevent caching
    const separator = url.includes('?') ? '&' : '?';
    processedUrl = `${url}${separator}t=${Date.now()}`;
  }
  
  // CORS proxy disabled - Firebase Storage should work directly with proper CORS configuration
  // If you need a CORS proxy, uncomment and configure one of the options below
  // Note: Most public CORS proxies require authentication or are unreliable
  
  return processedUrl;
};

/**
 * Fetches an image from a URL and converts it to a data URL to bypass CORS restrictions
 * @param {string} url - The original URL of the image to fetch
 * @param {string} [fallbackUrl] - Optional fallback URL to use if the original fails
 * @param {boolean} [isRetry=false] - Internal flag to prevent infinite retry loops
 * @returns {Promise<string>} - A promise that resolves to a data URL or blob URL
 */
export const proxyImage = async (url, fallbackUrl = null, isRetry = false) => {
  try {
    // Skip processing for blob URLs - they're already same-origin
    if (url && url.startsWith('blob:')) {
      return url;
    }
    
    // Skip processing for data URLs
    if (url && url.startsWith('data:')) {
      return url;
    }
    
    // Try using createBlobUrl first (direct fetch)
    try {
      return await createBlobUrl(url, fallbackUrl, isRetry);
    } catch (fetchError) {
      console.warn('Failed to fetch image directly, trying canvas method:', fetchError);
      
      // If it's a 404, don't try canvas method - the image doesn't exist
      if (fetchError.message && fetchError.message.includes('not found')) {
        throw fetchError;
      }
      
      // Fallback to canvas method for CORS issues
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Create a new image
        const img = new Image();
        
        // Create a promise that resolves when the image loads or rejects on error
        const imageLoadPromise = new Promise((resolve, reject) => {
          img.onload = () => {
            // Set canvas dimensions to match the image
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw the image on the canvas
            ctx.drawImage(img, 0, 0);
            
            // Convert the canvas to a data URL
            try {
              const dataURL = canvas.toDataURL('image/jpeg');
              resolve(dataURL);
            } catch (err) {
              reject(new Error('Failed to convert image to data URL'));
            }
          };
          
          img.onerror = () => {
            // If we have a fallback URL and we're not already in a retry, try the fallback
            if (fallbackUrl && !isRetry && fallbackUrl !== url) {
              console.log('Image load failed, trying fallback URL:', fallbackUrl);
              img.src = fallbackUrl;
            } else {
              reject(new Error('Failed to load image'));
            }
          };
        });
        
        // Set crossOrigin to anonymous to prevent CORS issues if possible
        img.crossOrigin = 'anonymous';
        const processedUrl = getCorsProxyUrl(url);
        img.src = processedUrl;
        
        // Wait for the image to load
        return await imageLoadPromise;
      } catch (canvasError) {
        console.error('Canvas method also failed:', canvasError);
        
        // If we have a fallback URL and we're not already in a retry, try the fallback
        if (fallbackUrl && !isRetry && fallbackUrl !== url) {
          console.log('Error occurred, trying fallback URL:', fallbackUrl);
          return proxyImage(fallbackUrl, null, true);
        }
        
        // If all methods fail, return the original URL and let the browser handle it
        // This may fail due to CORS, but it's better than throwing an error
        console.warn('All methods failed, returning original URL');
        return getCorsProxyUrl(url);
      }
    }
  } catch (error) {
    console.error('Error proxying image:', error);
    
    // If we have a fallback URL and we're not already in a retry, try the fallback
    if (fallbackUrl && !isRetry && fallbackUrl !== url) {
      console.log('Error occurred, trying fallback URL:', fallbackUrl);
      return proxyImage(fallbackUrl, null, true);
    }
    
    // If it's a "not found" error, rethrow it so the caller knows the image doesn't exist
    if (error.message && error.message.includes('not found')) {
      throw error;
    }
    
    // For other errors, return the URL anyway - let the browser try to load it
    return getCorsProxyUrl(url);
  }
};

/**
 * Creates a blob URL from an image URL to bypass CORS restrictions
 * @param {string} url - The URL of the image to fetch
 * @param {string} [fallbackUrl] - Optional fallback URL to use if the original fails
 * @param {boolean} [isRetry=false] - Internal flag to prevent infinite retry loops
 * @returns {Promise<string>} - A promise that resolves to a blob URL
 */
export const createBlobUrl = async (url, fallbackUrl = null, isRetry = false, options = {}) => {
  const {
    allowDirectUrlOnMobile = true,
  } = options || {};
  try {
    // Skip processing for blob URLs - they're already blob URLs
    if (url && url.startsWith('blob:')) {
      return url;
    }
    
    // Skip processing for data URLs - they're already data URLs
    if (url && url.startsWith('data:')) {
      return url;
    }
    
    // Check if we should use direct URLs (WeChat, adblocker, or restricted browsers)
    const useDirectUrl = await shouldUseDirectImageUrls();
    if (useDirectUrl && url && (url.includes('firebasestorage.googleapis.com') || url.includes('fotocarnetmadrid.firebasestorage.app'))) {
      // Return the direct URL with cache busting - img tags don't need CORS for display
      console.log('Using direct URL for restricted browser (WeChat/adblocker)');
      return getCorsProxyUrl(url);
    }
    
    // On mobile devices, prefer using direct URLs for Firebase Storage
    // Mobile browsers are more strict about CORS, but img tags work fine
    const isMobile = isMobileDevice();
    if (
      allowDirectUrlOnMobile &&
      isMobile &&
      url &&
      (url.includes('firebasestorage.googleapis.com') || url.includes('fotocarnetmadrid.firebasestorage.app'))
    ) {
      // Return the direct URL with cache busting - img tags don't need CORS for display
      return getCorsProxyUrl(url);
    }
    
    // Use the processed URL with cache busting
    const processedUrl = getCorsProxyUrl(url);
    
    try {
      // Try direct fetch first - Firebase Storage should work directly if CORS is configured
      // For WeChat and adblocker browsers, fetch might be blocked, so we'll catch and fallback
      const response = await fetch(processedUrl, {
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit', // Don't send credentials for cross-origin requests
        // Add headers that might help with adblockers
        headers: {
          'Accept': 'image/*,*/*',
        }
      });
      
      if (!response.ok) {
        // If it's a 404, the image doesn't exist yet (expected when processing service is not running)
        if (response.status === 404) {
          throw new Error('Image not found - processing may not be complete');
        }
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a blob URL
      return URL.createObjectURL(blob);
    } catch (error) {
      // In development mode, be less verbose about CORS errors
      const isDevMode = process.env.NODE_ENV === 'development';
      const isCorsError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('CORS') ||
        error.message.includes('NetworkError') ||
        error instanceof TypeError
      );
      
      // Only log non-CORS errors, or CORS errors in production
      if (!isCorsError || !isDevMode) {
        console.warn('Failed to fetch image directly:', error.message || error);
      }
      
      // If we have a fallback URL and we're not already in a retry, try the fallback
      if (fallbackUrl && !isRetry && fallbackUrl !== url) {
        if (!isDevMode || !isCorsError) {
          console.log('Trying fallback URL:', fallbackUrl);
        }
        return createBlobUrl(fallbackUrl, null, true, options); // Pass true to prevent further retries
      }
      
      // If it's a 404 or network error, rethrow it so the caller can handle it
      if (error.message && (error.message.includes('not found') || error.message.includes('404'))) {
        throw error;
      }
      
      // For CORS errors or fetch blocked (adblocker), try returning direct URL as fallback
      // This allows img tags to load the image directly (img tags don't need CORS for display)
      if (isCorsError) {
        // Check if this might be an adblocker or WeChat browser blocking fetch
        const isLikelyBlocked = isWeChatBrowser() || 
                                error.message.includes('Failed to fetch') ||
                                error.message.includes('NetworkError');
        
        if (isLikelyBlocked && url && (url.includes('firebasestorage.googleapis.com') || url.includes('fotocarnetmadrid.firebasestorage.app'))) {
          // For adblocker/WeChat browsers, return direct URL - img tags can load it
          console.log('Fetch blocked (likely adblocker/WeChat), using direct URL for img tag');
          return getCorsProxyUrl(url);
        }
        
        const corsError = new Error('CORS error: Failed to fetch image');
        corsError.isCorsError = true;
        corsError.originalError = error;
        corsError.url = processedUrl; // Include URL in error for debugging
        throw corsError;
      }
      
      // For other non-CORS errors, try returning the original URL as a last resort
      // Only in production - in dev mode, throw the error
      if (isDevMode) {
        throw error;
      }
      
      console.warn('Returning original URL as fallback - may fail due to CORS');
      return processedUrl;
    }
  } catch (error) {
    console.error('Error creating blob URL:', error);
    // If we have a fallback URL and we're not already in a retry, try the fallback
    if (fallbackUrl && !isRetry && fallbackUrl !== url) {
      console.log('Error occurred, trying fallback URL:', fallbackUrl);
      return createBlobUrl(fallbackUrl, null, true, options);
    }
    throw error;
  }
};